import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getRouteApi, useNavigate } from "@tanstack/react-router"

import type { AgentWorkspaceSearch } from "@/rootRoutes/agent-workspace-search"
import { routeFromMutationReceipt } from "../utils/app-routes"
import { isBackendConversationId } from "../utils/conversation-presenter"
import type { ChatPaneActions } from "../chat-pane"
import { useRobinConversationState } from "./use-robin-conversation-state"
import type { RobinMutationReceipt } from "../robin-types"

/**
 * Route ids are `joinPaths([parentRoute.id, customId])`, so the pathless
 * `agent-workspace` route under `authenticated` resolves to this. Taken as a
 * string rather than importing the route object, which would close the cycle
 * AgentWorkspaceRoute -> RobinWorkspace -> this hook.
 */
const workspaceRouteApi = getRouteApi("/authenticated/agent-workspace")

/**
 * `c` is written and cleared through `navigate` with function-form search, never
 * through `patchSearch`. `applySearchPatch` implements clearing as `delete`, which
 * leaves the key absent — and `retainSearchParams(["c"])` on the workspace route
 * would then helpfully put it straight back. An own property set to `undefined`
 * survives the middleware untouched and is dropped by the query encoder.
 */
function clearConversationSearch(prev: AgentWorkspaceSearch) {
  return { ...prev, c: undefined }
}

/**
 * Narrowed navigate, matching the shape `use-url-synced-search-state.ts` declares
 * for the same reason: the router's `NavigateOptions` generics collapse the search
 * reducer to `never` for a pathless route, so a function-form search write will not
 * typecheck against the raw result. That helper gets the same value passed as an
 * argument (parameter position, so it checks); here it has to be asserted, which
 * TypeScript only permits via `unknown`.
 */
type NavigateWithSearch = (options: {
  search: (prev: AgentWorkspaceSearch) => AgentWorkspaceSearch
  replace?: boolean
}) => void

export function useRobinWorkspace() {
  const navigate = useNavigate()
  const searchNavigate =
    workspaceRouteApi.useNavigate() as unknown as NavigateWithSearch
  const search = workspaceRouteApi.useSearch() as AgentWorkspaceSearch
  // Already normalized by `validateAgentWorkspaceSearch`. A junk `?c=` never
  // reaches here, and the router rewrites it out of the address bar on its own —
  // verified in `robin-conversation-url.spec.ts`, so no cleanup effect is needed.
  const urlConversationId = search.c

  // Read at fire time, not captured: hydration failures are async and the user
  // may have switched threads before one lands.
  const urlConversationIdRef = useRef<string | undefined>(urlConversationId)
  urlConversationIdRef.current = urlConversationId
  const ensureBlankChatRef = useRef<(() => void) | null>(null)

  const {
    conversations,
    selected,
    selectFromUrl,
    selectConversation,
    sendMessage,
    chooseOption,
    startNewChat,
    ensureBlankChat,
  } = useRobinConversationState({
    // The user picked a thread — a real navigation, so push. Back returns to the
    // thread they were reading before.
    onConversationSelected: useCallback(
      (id: string) => {
        if (!isBackendConversationId(id)) return
        searchNavigate({ search: (prev) => ({ ...prev, c: id }) })
      },
      [searchNavigate],
    ),
    // A local draft just became a backend conversation mid-send. Not a
    // navigation, so replace — otherwise Back lands on a draft that no longer
    // exists.
    onConversationPersisted: useCallback(
      (id: string) => {
        searchNavigate({ search: (prev) => ({ ...prev, c: id }), replace: true })
      },
      [searchNavigate],
    ),
    onFreshChatSelected: useCallback(() => {
      searchNavigate({ search: clearConversationSearch })
    }, [searchNavigate]),
    onConversationUnavailable: useCallback(
      (failedId: string) => {
        // Only act if the dead id is still the one showing, or we would clear a
        // perfectly good conversation out from under the user.
        if (urlConversationIdRef.current !== failedId) return
        searchNavigate({ search: clearConversationSearch, replace: true })
        // Deliberately not `startNewChat()` — that fires `onFreshChatSelected`,
        // which would push a second history entry on top of this replace.
        ensureBlankChatRef.current?.()
      },
      [searchNavigate],
    ),
  })

  ensureBlankChatRef.current = ensureBlankChat

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  useEffect(() => {
    document.title = "Assistant · RecruiterOS"
  }, [])

  // URL -> state. The URL is the source of truth for which conversation is open.
  useEffect(() => {
    if (urlConversationId === selected.id) return
    // No `c` and we are already on a local draft: nothing to do. Without this,
    // going Back to a bare URL would churn the draft on every render.
    if (!urlConversationId && !isBackendConversationId(selected.id)) return
    selectFromUrl(urlConversationId)
  }, [urlConversationId, selected.id, selectFromUrl])

  const openMutationReceipt = useCallback(
    (receipt: RobinMutationReceipt) => {
      const route = routeFromMutationReceipt(receipt)
      if (!route) return
      void navigate({ to: route })
    },
    [navigate],
  )

  const actions = useMemo<ChatPaneActions>(
    () => ({
      onSend: (text, files) => sendMessage(selected.id, text, files),
      onChoose: (attachmentId, choiceId) =>
        chooseOption(selected.id, attachmentId, choiceId),
      onOpenReceipt: openMutationReceipt,
    }),
    [chooseOption, openMutationReceipt, selected.id, sendMessage],
  )

  const handleSelectConversation = useCallback(
    (id: string) => {
      selectConversation(id)
      setMobileSidebarOpen(false)
    },
    [selectConversation],
  )

  const handleNewChat = useCallback(() => {
    startNewChat()
    setMobileSidebarOpen(false)
  }, [startNewChat])

  return {
    conversations,
    selected,
    actions,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    handleSelectConversation,
    handleNewChat,
  }
}
